import { useCallback } from 'react';
import { useMatches } from '@/modules/matches/api';
import * as Sentry from '@sentry/browser';

/**
 * Hook to manage saving match data after a game ends
 */
export function useMatchDataManager() {
  const { saveMatch } = useMatches();

  const saveMatchData = useCallback(async (gameState, playerData) => {
    try {
      // Defensive validation with detailed error context
      if (!gameState) {
        const error = new Error('Game state is missing or null');
        console.error('Save match failed - missing game state:', { gameState, playerData });
        Sentry.captureException(error, {
          extra: {
            gameStateProvided: !!gameState,
            playerDataProvided: !!playerData,
            playerDataLength: playerData?.length || 0,
            context: 'saveMatchData validation'
          }
        });
        throw error;
      }

      if (!playerData) {
        const error = new Error('Player data is missing or null');
        console.error('Save match failed - missing player data:', { gameState, playerData });
        Sentry.captureException(error, {
          extra: {
            gameStateProvided: !!gameState,
            playerDataProvided: !!playerData,
            gameStateKeys: gameState ? Object.keys(gameState) : [],
            context: 'saveMatchData validation'
          }
        });
        throw error;
      }

      if (!Array.isArray(playerData)) {
        const error = new Error('Player data must be an array');
        console.error('Save match failed - player data is not an array:', { 
          gameState, 
          playerData, 
          playerDataType: typeof playerData 
        });
        Sentry.captureException(error, {
          extra: {
            playerDataType: typeof playerData,
            playerDataValue: playerData,
            context: 'saveMatchData validation'
          }
        });
        throw error;
      }

      console.log('Preparing to save match data...', {
        gameStateKeys: Object.keys(gameState),
        playerCount: playerData.length
      });

      // Get opponent name from user input or default
      const opponentName = localStorage.getItem('opponent_name') || 'Unknown Opponent';
      
      // Get current squad ID if available
      const currentSquadId = localStorage.getItem('current_squad_id') || null;

      // Calculate total game duration
      let totalDuration = 0;
      if (gameState.gameIntervals && Array.isArray(gameState.gameIntervals)) {
        gameState.gameIntervals.forEach((interval) => {
          if (interval && typeof interval === 'object' && typeof interval.endTime === 'number' && typeof interval.startTime === 'number') {
            totalDuration += interval.endTime - interval.startTime;
          }
        });
      }
      totalDuration = Math.floor(totalDuration / 1000); // Convert to seconds

      // Calculate player statistics with validation
      const playerStats = playerData
        .filter(player => {
          if (!player || typeof player !== 'object') {
            console.warn('Filtering out invalid player (not an object):', player);
            return false;
          }
          if (!player.name || typeof player.name !== 'string' || player.name.trim() === '') {
            console.warn('Filtering out player with invalid name:', player);
            return false;
          }
          return true;
        })
        .map(player => {
          let playtimeSeconds = 0;
          let wasGoalkeeper = false;
          let goalsScored = 0;

          // Calculate playtime from intervals with validation
          if (player.playIntervals && Array.isArray(player.playIntervals)) {
            player.playIntervals.forEach(interval => {
              if (interval && typeof interval === 'object' && 
                  typeof interval.endTime === 'number' && 
                  typeof interval.startTime === 'number') {
                playtimeSeconds += interval.endTime - interval.startTime;
                if (interval.isGoalkeeper) {
                  wasGoalkeeper = true;
                }
              }
            });
          }
          playtimeSeconds = Math.floor(playtimeSeconds / 1000); // Convert to seconds

          // Count goals scored by this player with validation
          if (gameState.goals && Array.isArray(gameState.goals)) {
            goalsScored = gameState.goals.filter(goal => {
              return goal && 
                     goal.team === 'our' && 
                     goal.scorerName === player.name;
            }).length;
          }

          return {
            name: player.name,
            playtimeSeconds,
            wasGoalkeeper,
            goalsScored
          };
        });

      // Validate we have at least some player stats
      if (playerStats.length === 0) {
        const error = new Error('No valid players found in player data');
        console.error('Save match failed - no valid players:', { 
          originalPlayerData: playerData,
          filteredPlayerStats: playerStats
        });
        Sentry.captureException(error, {
          extra: {
            originalPlayerCount: playerData.length,
            validPlayerCount: playerStats.length,
            playerData: playerData,
            context: 'saveMatchData player validation'
          }
        });
        throw error;
      }

      // Prepare match data with defaults
      const matchData = {
        squadId: currentSquadId,
        opponentName,
        ourScore: typeof gameState.ourScore === 'number' ? gameState.ourScore : 0,
        opponentScore: typeof gameState.opponentScore === 'number' ? gameState.opponentScore : 0,
        matchDate: new Date().toISOString(),
        gameDurationSeconds: totalDuration,
        goals: Array.isArray(gameState.goals) ? gameState.goals : []
      };

      console.log('Saving match data:', {
        matchData,
        playerStatsCount: playerStats.length,
        totalDuration,
        opponentName
      });

      // Save to database
      const result = await saveMatch(matchData, playerStats);
      
      console.log('Match saved successfully:', result.matchId);
      
      return result;
    } catch (error) {
      console.error('Error saving match data:', error);
      
      // Enhanced error reporting
      Sentry.captureException(error, {
        extra: {
          gameState: gameState ? {
            hasOurScore: 'ourScore' in gameState,
            hasOpponentScore: 'opponentScore' in gameState,
            hasGoals: 'goals' in gameState,
            hasGameIntervals: 'gameIntervals' in gameState,
            goalsLength: Array.isArray(gameState.goals) ? gameState.goals.length : 'not array',
            intervalsLength: Array.isArray(gameState.gameIntervals) ? gameState.gameIntervals.length : 'not array'
          } : null,
          playerDataInfo: playerData ? {
            isArray: Array.isArray(playerData),
            length: playerData.length || 0,
            firstPlayerStructure: playerData[0] ? Object.keys(playerData[0]) : null
          } : null,
          context: 'useMatchDataManager.saveMatchData'
        }
      });
      
      throw error;
    }
  }, [saveMatch]);

  return {
    saveMatchData
  };
}